// ── DisputeCard ───────────────────────────────────────────────────────────────

import { ExternalLink } from "lucide-react";
import type { Transaction } from "@/application/money/types";
import { formatCurrency, formatAbsolute } from "@/application/money/types";
import { disputeUrl } from "@/lib/stripe-links";
import { CopyButton } from "./CopyButton";

interface DisputeCardProps {
  transaction: Transaction;
}

export function DisputeCard({ transaction: tx }: DisputeCardProps) {
  if (tx.dispute == null) return null;

  const dispute = tx.dispute;
  // dispute.amount is typed as float but represents cents (per spec §11)
  const disputeAmountCents = Math.round(dispute.amount);
  const disputeStripeUrl = dispute.id ? disputeUrl(dispute.id) : null;

  return (
    <div className="bg-card rounded-lg border border-l-4 border-l-red-400 p-5">
      <h2 className="mb-3 text-sm font-semibold text-red-700">Dispute</h2>

      <div className="divide-y text-sm">
        <div className="flex justify-between py-2.5">
          <span className="text-muted-foreground">Status</span>
          <span className="text-foreground font-medium capitalize">
            {dispute.status?.replace(/_/g, " ") ?? "—"}
          </span>
        </div>

        <div className="flex justify-between py-2.5">
          <span className="text-muted-foreground">Amount</span>
          <span className="text-foreground font-medium">
            {formatCurrency(
              disputeAmountCents,
              dispute.currency ?? tx.currency,
            )}
          </span>
        </div>

        {dispute.created != null && (
          <div className="flex justify-between py-2.5">
            <span className="text-muted-foreground">Created</span>
            <span className="text-foreground">
              {formatAbsolute(dispute.created)}
            </span>
          </div>
        )}

        {dispute.reason && (
          <div className="flex justify-between py-2.5">
            <span className="text-muted-foreground">Reason</span>
            <span className="text-foreground capitalize">
              {dispute.reason.replace(/_/g, " ")}
            </span>
          </div>
        )}

        {dispute.id && (
          <div className="flex justify-between py-2.5">
            <span className="text-muted-foreground">Dispute ID</span>
            <span className="text-foreground flex items-center gap-1 font-mono text-xs">
              {dispute.id}
              <CopyButton value={dispute.id} />
              {disputeStripeUrl && (
                <a
                  href={disputeStripeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                  aria-label="View dispute in Stripe"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
